import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Backdrop,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Collapse,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import Modal from './Modal';
import ReviewSubmitPanel from './ReviewSubmitPanel';
import { AnomalyReextractionChip } from './ReextractionChip';
import { useEvidenceViewerState } from '../hooks/useEvidenceViewerState';
import { isIncompleteFalsePositive, FP_REASON_REQUIRED, getPresentReviewStats } from '../utils/reviewClassification';
import { issueTypeLabel } from './deterministic/issueTypeRenderers';

const CATEGORY_COLORS = {
  thumbnail_instead_of_full: '#a855f7',
  image_count_drop: '#f59e0b',
  bp_count_dropped: '#f59e0b',
  bp_nav_footer_content: '#ef4444',
  html_or_code: '#ef4444',
  malformed_number: '#ef4444',
  ppu_has_duplicate_content: '#f97316',
  comments_dropped_to_zero: '#ef4444',
  comments_crashed: '#ef4444',
  st_flipped: '#8b5cf6',
  drastic_change: '#f59e0b',
  encoding_issue: '#ef4444',
};

function getCategoryColor(cat) {
  return CATEGORY_COLORS[cat] || '#64748b';
}

function getChangedIndices(initial, current) {
  const len = Math.max(initial.length, current.length);
  const changed = [];
  for (let i = 0; i < len; i++) {
    const a = initial[i] || { verdict: '', fpReason: '', scope_type: 'retailer_specific' };
    const b = current[i] || { verdict: '', fpReason: '', scope_type: 'retailer_specific' };
    if (a.verdict !== b.verdict || a.fpReason !== b.fpReason || a.scope_type !== b.scope_type) changed.push(i);
  }
  return changed;
}

/**
 * Unified evidence-review modal for both Null (`issue_type: 'null-issues'`)
 * and Non-Null (`issue_type: 'non-null-issues'`) anomalies. Replaces the
 * former ImagePreviewModal.js and NonNullEvidenceModal.js -- see
 * docs/plans/2026-09-18-001-unify-evidence-modals-plan.md for the full
 * rationale. Every genuinely different piece of UI between the two former
 * components is gated on the explicit `issueType` prop below; everything
 * else (header controls, zoom, occurrence dropdown, ReviewSubmitPanel,
 * detail sections that already gate on field presence, thumbnail strip) is
 * one shared render path.
 */
function EvidenceReviewModal({
  isOpen, onClose, evidenceList, deepLinkTarget, initialShowPresentOnly,
  sampleClassifications, onUpdateVerdict, onUpdateFpReason, onPersistClassifications,
  anomalyId, retailer, attribute, pageType,
  // DPS-4718: who recorded the anomaly's verdict (server-derived), or null.
  verifiedBy = null,
  // 'null-issues' | 'non-null-issues' -- which anomaly type this instance is
  // reviewing. Explicit, not re-derived from evidence shape (KTD2).
  issueType,
  // Reports a user-driven occurrence switch back up so the parent can
  // reflect it in the URL (`?occurrence=`). `onInvalidOccurrence` fires when
  // a deep-linked occurrence id is not in the fetched list, so the parent
  // can strip the stale param with history.replace.
  onOccurrenceChange, onInvalidOccurrence,
}) {
  const isNonNull = issueType === 'non-null-issues';

  const [zoom, setZoom] = useState(1);
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const imageContainerRef = useRef(null);

  const {
    currentIndex: hookCurrentIndex,
    setCurrentIndex: hookSetCurrentIndex,
    activeEvidence: allItems,
    filteredIndices,
    showPresentOnly, setShowPresentOnly,
    occurrences, selectedOccurrenceId,
    selectOccurrence: hookSelectOccurrence,
    localClassifications, localClassificationsRef, initialClassificationsRef,
    setVerdict, setFpReason, setScopeType,
    isLoading,
  } = useEvidenceViewerState({
    isOpen,
    anomalyId,
    evidenceList,
    sampleClassifications,
    deepLinkTarget,
    initialShowPresentOnly,
    onPersistClassifications,
    // NonNullEvidenceModal's original behavior; ImagePreviewModal never had
    // this guard, so it stays off for Null anomalies (see the hook's own
    // doc comment on this option).
    requireLatestOccurrenceClassifications: isNonNull,
  });

  // `currentIndex` here is the POSITION within `filteredIndices` (used only
  // for the "N of M" display and to drive the thumbnail strip for Non-Null);
  // `realIndex` is the REAL index into `allItems` -- what classification
  // reads/writes and the ReviewSubmitPanel submit payload must use.
  //
  // Deliberately derived via `filteredIndices[currentIndex] ?? hookCurrentIndex`
  // rather than read directly off `hookCurrentIndex` -- this is the one spot
  // NonNullEvidenceModal's original translation layer got wrong (it used
  // raw `hookCurrentIndex` for `realIndex`), which silently pointed
  // classification reads/writes at the wrong sample whenever the hook's
  // seeded index wasn't itself a member of `filteredIndices` (see
  // docs/handoffs/unify-evidence-review-modal-unit-1.md for the full case).
  // Deriving both from the same expression makes that divergence impossible
  // by construction, independent of whether the hook's own seeding is
  // exactly right.
  const currentIndex = Math.max(0, filteredIndices.indexOf(hookCurrentIndex));
  const realIndex = filteredIndices[currentIndex] ?? hookCurrentIndex;

  const filteredItems = useMemo(
    () => filteredIndices.map((i) => allItems[i]),
    [filteredIndices, allItems]
  );

  const currentEvidence = filteredItems[currentIndex] || null;

  const handleOccurrenceChange = useCallback(async (occId) => {
    if (isIncompleteFalsePositive(localClassificationsRef.current?.[realIndex])) {
      return;
    }
    await hookSelectOccurrence(occId);
    onOccurrenceChange?.(occId);
  }, [hookSelectOccurrence, onOccurrenceChange, realIndex, localClassificationsRef]);

  useEffect(() => {
    if (!isOpen || !deepLinkTarget?.occurrenceId || occurrences.length === 0) return;
    if (!occurrences.some((o) => o.id === deepLinkTarget.occurrenceId)) {
      onInvalidOccurrence?.();
    }
  }, [isOpen, occurrences, deepLinkTarget, onInvalidOccurrence]);

  // Occurrences are ordered newest-first by the API; when the user hasn't
  // explicitly picked one via the dropdown, the "current" one being viewed
  // is the latest. Passed to ReviewSubmitPanel as the review-signal target.
  const effectiveOccurrenceId = selectedOccurrenceId || occurrences[0]?.id || null;

  const currentClassification = localClassifications[realIndex] || {};
  const currentNeedsFpReason = isIncompleteFalsePositive(currentClassification);

  // Shared by handleClose (X button) and the URL-driven close effect below
  // (browser back / a param removed elsewhere) so a pending classification
  // edit is saved either way instead of only when the X is clicked.
  const flushPendingClassifications = useCallback(async () => {
    if ((localClassificationsRef.current || []).some(isIncompleteFalsePositive)) {
      return false;
    }
    const changedIndices = getChangedIndices(initialClassificationsRef.current, localClassificationsRef.current);
    // Sync local state to parent (only changed entries) -- onUpdateVerdict/
    // onUpdateFpReason are Null-only today; guarded so this is a no-op when
    // a caller (e.g. Non-Null) doesn't supply them.
    if (onUpdateVerdict && localClassificationsRef.current && changedIndices.length > 0) {
      changedIndices.forEach((idx) => {
        const cls = localClassificationsRef.current[idx];
        const orig = initialClassificationsRef.current[idx];
        if (cls?.verdict !== orig?.verdict) onUpdateVerdict(idx, cls.verdict);
        if (cls?.fpReason !== orig?.fpReason) onUpdateFpReason(idx, cls.fpReason);
      });
    }
    // Persist to backend only if something changed
    if (onPersistClassifications && changedIndices.length > 0) {
      setSaving(true);
      try {
        await onPersistClassifications(localClassificationsRef.current, selectedOccurrenceId);
      } catch {
        // Silently continue — user can re-classify later
      } finally {
        setSaving(false);
      }
    }
    return true;
  }, [onUpdateVerdict, onUpdateFpReason, onPersistClassifications, selectedOccurrenceId]);

  // Guards the URL-driven close effect below from double-flushing right
  // after a handleClose-initiated close (handleClose already flushed before
  // `onClose()` propagates back down as `isOpen` turning false).
  const closeHandledRef = useRef(false);

  const handleClose = useCallback(async () => {
    const saved = await flushPendingClassifications();
    if (!saved) return;
    closeHandledRef.current = true;
    onClose();
  }, [onClose, flushPendingClassifications]);

  // Catches every OTHER way the modal can end up closed — most importantly,
  // browser back/forward or a hand-edited URL removing the `anomaly` param,
  // which flips `isOpen` to false without ever calling handleClose. Without
  // this, a pending verdict picked just before pressing back would be
  // dropped silently.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      if (!closeHandledRef.current) {
        flushPendingClassifications();
      }
      closeHandledRef.current = false;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, flushPendingClassifications]);

  const handleResetView = useCallback(() => {
    setZoom(1);
    if (imageContainerRef.current) {
      imageContainerRef.current.scrollTop = 0;
      imageContainerRef.current.scrollLeft = 0;
    }
  }, []);

  const handlePrevious = useCallback(() => {
    if (isIncompleteFalsePositive(localClassificationsRef.current?.[realIndex])) return;
    if (filteredIndices.length === 0) return;
    const currentPos = filteredIndices.indexOf(realIndex);
    const prevPos = currentPos > 0 ? currentPos - 1 : filteredIndices.length - 1;
    hookSetCurrentIndex(filteredIndices[prevPos]);
    handleResetView();
  }, [filteredIndices, realIndex, hookSetCurrentIndex, handleResetView, localClassificationsRef]);

  const handleNext = useCallback(() => {
    if (isIncompleteFalsePositive(localClassificationsRef.current?.[realIndex])) return;
    if (filteredIndices.length === 0) return;
    const currentPos = filteredIndices.indexOf(realIndex);
    const nextPos = currentPos < filteredIndices.length - 1 ? currentPos + 1 : 0;
    hookSetCurrentIndex(filteredIndices[nextPos]);
    handleResetView();
  }, [filteredIndices, realIndex, hookSetCurrentIndex, handleResetView, localClassificationsRef]);

  // Collapse the reasoning panel and reset zoom on open and on occurrence
  // switch -- matches NonNullEvidenceModal's original reset-on-evidence-
  // context-change; per-sample navigation (prev/next/thumbnail click)
  // already calls handleResetView() directly at its own call sites above.
  // Harmless no-op for Null (its Reasoning section never reads
  // reasoningExpanded).
  useEffect(() => {
    setReasoningExpanded(false);
    handleResetView();
  }, [isOpen, selectedOccurrenceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingZoomScrollRef = useRef(null);

  const handleClickToggleZoom = useCallback((e) => {
    const container = imageContainerRef.current;
    const nextZoom = zoom === 1 ? 2 : 1;

    if (!container) {
      setZoom(nextZoom);
      return;
    }

    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const scale = nextZoom / zoom;

    // Preserve the clicked content point under the cursor after width rescale.
    pendingZoomScrollRef.current = {
      left: (container.scrollLeft + clickX) * scale - clickX,
      top: (container.scrollTop + clickY) * scale - clickY,
    };
    setZoom(nextZoom);
  }, [zoom]);

  useLayoutEffect(() => {
    const pending = pendingZoomScrollRef.current;
    const container = imageContainerRef.current;
    if (!pending || !container) return;
    pendingZoomScrollRef.current = null;
    container.scrollLeft = Math.max(0, pending.left);
    container.scrollTop = Math.max(0, pending.top);
  }, [zoom]);

  const handleWheel = useCallback((e) => {
    // Native scroll by default; Ctrl/Cmd+wheel zooms width.
    if (!(e.ctrlKey || e.metaKey)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.25 : 0.25;
    setZoom((prev) => {
      const next = Math.max(1, Math.min(4, prev + delta));
      return next;
    });
  }, []);

  // Prevent browser's native pinch-to-zoom only within the image container
  useEffect(() => {
    if (!isOpen) return;

    const container = imageContainerRef.current;
    if (!container) return;

    const preventZoomInContainer = (e) => {
      // Prevent browser page zoom; React onWheel applies our zoom.
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const preventGestureZoom = (e) => {
      e.preventDefault();
    };

    // Only add listeners to the image container, not the whole document
    container.addEventListener('wheel', preventZoomInContainer, { passive: false });
    container.addEventListener('gesturestart', preventGestureZoom);
    container.addEventListener('gesturechange', preventGestureZoom);
    container.addEventListener('gestureend', preventGestureZoom);

    return () => {
      container.removeEventListener('wheel', preventZoomInContainer);
      container.removeEventListener('gesturestart', preventGestureZoom);
      container.removeEventListener('gesturechange', preventGestureZoom);
      container.removeEventListener('gestureend', preventGestureZoom);
    };
  }, [isOpen]);

  const screenshotUrl = currentEvidence?.url || null;
  const hasScreenshot = !!screenshotUrl;
  // The former per-modal nav-button visibility gates genuinely differed
  // (ImagePreviewModal gated on the full unfiltered list, NonNullEvidenceModal
  // on the filtered one) -- preserved exactly rather than silently picked one
  // for the other.
  const canNavigate = isNonNull ? filteredItems.length > 1 : allItems.length > 1;

  // Thumbnail strip data source also genuinely differs: Null shows every
  // item (including filtered-out ones, distinguished by verdict color);
  // Non-Null shows only the currently filtered set. Preserved per issueType
  // rather than picking one.
  const stripItems = useMemo(() => {
    if (isNonNull) {
      return filteredItems.map((item, idx) => ({ item, realIdx: filteredIndices[idx] ?? idx }));
    }
    return allItems.map((item, idx) => ({ item, realIdx: idx }));
  }, [isNonNull, filteredItems, filteredIndices, allItems]);

  const heading = (
    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, fontWeight: 600 }}>
      {(attribute || retailer) && (
        <Box component="span">
          {attribute && (
            <>
              <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>Attribute: </Box>
              <Box component="span" sx={{ color: '#f1f5f9', fontWeight: 700 }}>{attribute.toUpperCase()}</Box>
            </>
          )}
          {attribute && retailer ? <Box component="span" sx={{ color: '#64748b', mx: 1.5 }}>·</Box> : null}
          {retailer && (
            <>
              <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>Retailer : </Box>
              <Box component="span" sx={{ color: '#f1f5f9', fontWeight: 700 }}>{retailer}</Box>
            </>
          )}
        </Box>
      )}
      <Chip
        label={`${issueTypeLabel(issueType)} Issue`}
        size="small"
        color={isNonNull ? 'warning' : 'info'}
        variant="outlined"
      />
      {verifiedBy && (
        <Box component="span" data-testid="evidence-verified-by" sx={{ fontSize: '0.8rem' }}>
          <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>Verified by: </Box>
          <Box component="span" sx={{ color: '#f1f5f9', fontWeight: 600 }}>{verifiedBy}</Box>
        </Box>
      )}
      {isOpen && <AnomalyReextractionChip anomalyId={anomalyId} />}
    </Typography>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={heading}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '92vh', overflow: 'hidden', touchAction: 'none', position: 'relative' }}>
        {/* Saving overlay */}
        <Backdrop
          open={saving}
          sx={{
            position: 'absolute',
            zIndex: 1300,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={36} sx={{ color: '#3b82f6' }} />
          <Typography sx={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: 500 }}>
            Saving classifications...
          </Typography>
        </Backdrop>
        {/* Header with controls */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600, lineHeight: 1.3 }}>
              Evidence Preview
            </Typography>
            {filteredIndices.length > 0 && (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                {currentIndex + 1} of {filteredIndices.length}
                {showPresentOnly && filteredIndices.length < allItems.length && (
                  <span style={{ color: '#64748b' }}> ({allItems.length} total)</span>
                )}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showPresentOnly}
                  onChange={() => setShowPresentOnly((prev) => !prev)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                  Show present only
                </Typography>
              }
              sx={{ ml: 0, mr: 0 }}
            />
            {occurrences.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedOccurrenceId || ''}
                  displayEmpty
                  disabled={currentNeedsFpReason}
                  onChange={(e) => handleOccurrenceChange(e.target.value || null)}
                  sx={{
                    color: '#e2e8f0',
                    fontSize: '0.8rem',
                    '& .MuiSelect-icon': { color: '#94a3b8' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  }}
                  startAdornment={<HistoryIcon sx={{ color: '#94a3b8', mr: 0.5, fontSize: '1rem' }} />}
                >
                  <MenuItem value="">Latest evidence</MenuItem>
                  {occurrences.map((occ) => {
                    let progressLabel = null;
                    if (occ.sample_classifications) {
                      try {
                        const cls = JSON.parse(occ.sample_classifications);
                        let evidence = [];
                        if (occ.evidence_with_reasoning) {
                          try { evidence = JSON.parse(occ.evidence_with_reasoning); } catch { evidence = []; }
                        }
                        const { reviewed, total } = getPresentReviewStats(cls, evidence);
                        if (reviewed === total && total > 0) {
                          progressLabel = <span style={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 600 }}>✓ {reviewed}/{total}</span>;
                        } else if (reviewed > 0 || total > 0) {
                          progressLabel = <span style={{ color: '#d97706', fontSize: '0.7rem', fontWeight: 600 }}>{reviewed}/{total} reviewed</span>;
                        }
                      } catch { /* ignore */ }
                    }
                    return (
                      <MenuItem key={occ.id} value={occ.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <span>{new Date(occ.occurred_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        {progressLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Zoom controls */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              px: 2,
              py: 0.5
            }}>
              <IconButton onClick={() => setZoom((z) => Math.max(1, z - 0.5))} size="small" sx={{ color: '#94a3b8' }} disabled={zoom <= 1}>
                <ZoomOutIcon />
              </IconButton>
              <Typography variant="body2" sx={{ color: '#94a3b8', minWidth: 50, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton onClick={() => setZoom((z) => Math.min(4, z + 0.5))} size="small" sx={{ color: '#94a3b8' }} disabled={zoom >= 4}>
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={handleResetView} size="small" sx={{ color: '#94a3b8' }} disabled={zoom === 1}>
                <RestartAltIcon />
              </IconButton>
            </Box>

            <IconButton
              onClick={() => currentEvidence?.url && window.open(currentEvidence.url, '_blank')}
              size="small"
              sx={{ color: '#3b82f6' }}
              title="Open in new tab"
              disabled={!currentEvidence?.url}
            >
              <OpenInNewIcon />
            </IconButton>

            <IconButton onClick={handleClose} size="small" sx={{ color: '#94a3b8' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Click to zoom hint */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 0.5,
          background: 'rgba(255, 255, 255, 0.02)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Scroll to navigate • Click image or use controls to zoom • Ctrl/Cmd+scroll to zoom
          </Typography>
        </Box>

        {/* Main content area */}
        {!currentEvidence ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <BrokenImageIcon sx={{ fontSize: 48, color: '#334155' }} />
            <Typography sx={{ color: '#94a3b8' }}>No evidence samples available</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
            {/* Loading overlay when switching occurrences, or while a deep link's
                target occurrence is queued/being fetched -- covers the gap between
                the modal opening and that fetch actually starting, not just its
                in-flight duration. */}
            {isLoading && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,10,10,0.85)' }}>
                <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
              </Box>
            )}
            {/* Image panel */}
            {hasScreenshot ? (
              <Box
                sx={{
                  flex: '1 1 60%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#0a0a0a',
                  overflow: 'hidden'
                }}
              >
                {/* Navigation buttons */}
                {canNavigate && (
                  <>
                    <Tooltip title={currentNeedsFpReason ? FP_REASON_REQUIRED : ''}>
                      <span>
                        <IconButton
                          onClick={handlePrevious}
                          disabled={currentNeedsFpReason}
                          sx={{
                            position: 'absolute',
                            left: 16,
                            zIndex: 10,
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': { background: 'rgba(0, 0, 0, 0.8)' },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)', background: 'rgba(0, 0, 0, 0.4)' },
                          }}
                        >
                          <NavigateBeforeIcon fontSize="large" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={currentNeedsFpReason ? FP_REASON_REQUIRED : ''}>
                      <span>
                        <IconButton
                          onClick={handleNext}
                          disabled={currentNeedsFpReason}
                          sx={{
                            position: 'absolute',
                            right: 16,
                            zIndex: 10,
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': { background: 'rgba(0, 0, 0, 0.8)' },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)', background: 'rgba(0, 0, 0, 0.4)' },
                          }}
                        >
                          <NavigateNextIcon fontSize="large" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}

                {/* Image container */}
                <Box
                  ref={imageContainerRef}
                  onClick={handleClickToggleZoom}
                  onWheel={handleWheel}
                  sx={{
                    cursor: zoom === 1 ? 'zoom-in' : 'zoom-out',
                    overflow: 'auto',
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    touchAction: 'pan-x pan-y',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    '&::-webkit-scrollbar': { width: 8, height: 8 },
                    '&::-webkit-scrollbar-thumb': { background: '#475569', borderRadius: 4 }
                  }}
                >
                  <Box
                    component="img"
                    src={screenshotUrl}
                    alt="Evidence screenshot"
                    draggable={false}
                    sx={{
                      width: `${zoom * 100}%`,
                      maxWidth: 'none',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23666" font-size="14">Image failed to load</text></svg>';
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: '1 1 60%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', flexDirection: 'column', gap: 1 }}>
                <BrokenImageIcon sx={{ fontSize: 48, color: '#334155' }} />
                <Typography sx={{ color: '#475569' }}>No screenshot available</Typography>
                {canNavigate && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Tooltip title={currentNeedsFpReason ? FP_REASON_REQUIRED : ''}>
                      <span>
                        <IconButton disabled={currentNeedsFpReason} onClick={handlePrevious} sx={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}><NavigateBeforeIcon /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={currentNeedsFpReason ? FP_REASON_REQUIRED : ''}>
                      <span>
                        <IconButton disabled={currentNeedsFpReason} onClick={handleNext} sx={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}><NavigateNextIcon /></IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}

            {/* Reasoning panel */}
            <Box
              sx={{
                flex: '1 1 40%',
                borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                background: '#111827'
              }}
            >
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ color: '#e2e8f0', fontWeight: 600, lineHeight: 1.3 }}>
                  Analysis Details
                </Typography>
              </Box>

              {/* Sample Classification — pinned at top. ReviewSubmitPanel is the
                  single review-submit system shared across issue types; it
                  self-gates on non-absent presence and `editable` only —
                  chips, reason, scope and Submit Review all render for every
                  page type and issue type. What rule-synthesis-service
                  actually consumes (DETAIL + null-issues) is filtered on the
                  consumer side, not here. */}
              <ReviewSubmitPanel
                editable={!!(onUpdateVerdict || onPersistClassifications)}
                pageType={pageType}
                presence={currentEvidence.presence}
                anomalyId={anomalyId}
                occurrenceId={effectiveOccurrenceId}
                evidenceIndex={realIndex}
                classification={currentClassification}
                classifications={localClassifications}
                evidenceList={allItems}
                onVerdictChange={(verdict) => setVerdict(realIndex, verdict)}
                onFpReasonChange={(reason) => setFpReason(realIndex, reason)}
                onScopeTypeChange={(scopeType) => setScopeType(realIndex, scopeType)}
              />

              <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                {/* Detection method */}
                {currentEvidence.detected_via && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Detected Via
                    </Typography>
                    <Paper sx={{
                      mt: 0.5,
                      p: 1,
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}>
                      <Typography sx={{ color: '#cbd5e1', fontWeight: 500 }}>
                        {currentEvidence.detected_via}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Presence status */}
                {currentEvidence.presence && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Attribute Presence
                    </Typography>
                    <Paper sx={{
                      mt: 0.5,
                      p: 1,
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: currentEvidence.presence === 'present' ? '#10b981' : '#ef4444',
                      }} />
                      <Typography sx={{ color: '#cbd5e1', fontWeight: 500, textTransform: 'capitalize' }}>
                        {currentEvidence.presence}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Extracted Value -- genuinely different styling per issue
                    type (KTD1/R3): Null shows attribute_value with a
                    page_value fallback in neutral styling (search null
                    historically stored the find in page_value only); Non-Null
                    shows attribute_value only, in red, since Non-Null has a
                    separate "Page Value (Expected)" section below. */}
                {isNonNull ? (
                  currentEvidence.attribute_value && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Extracted Value
                      </Typography>
                      <Paper sx={{
                        mt: 0.5, p: 1,
                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px',
                        maxHeight: 120, overflow: 'auto',
                      }}>
                        <Typography sx={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {currentEvidence.attribute_value}
                        </Typography>
                      </Paper>
                    </Box>
                  )
                ) : (
                  (currentEvidence.attribute_value || currentEvidence.page_value) && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Extracted Value
                      </Typography>
                      <Paper sx={{
                        mt: 0.5,
                        p: 1,
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px'
                      }}>
                        <Typography sx={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-word' }}>
                          {currentEvidence.attribute_value || currentEvidence.page_value}
                        </Typography>
                      </Paper>
                    </Box>
                  )
                )}

                {/* Page Value (Expected) — non-null specific. Explicitly
                    gated on isNonNull (not just field presence, per code
                    review): `page_value` is also populated on `null-issues`
                    evidence from the search-page NULL-detection pipeline
                    (anomaly-detection-agent/app/search/nodes.py), so a plain
                    presence check would leak this Non-Null-styled/worded
                    section onto Null anomalies too. */}
                {isNonNull && (currentEvidence.page_value
                  || currentEvidence.reason_evaluations?.some(
                    (re) => re.validated && (
                      (re.category || '').includes('hero_product')
                      || (re.reason || '').includes('hero_product')
                    ),
                  )) && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Page Value (Expected)
                    </Typography>
                    <Paper sx={{
                      mt: 0.5, p: 1,
                      background: currentEvidence.page_value
                        ? 'rgba(16,185,129,0.06)'
                        : 'rgba(245,158,11,0.06)',
                      border: currentEvidence.page_value
                        ? '1px solid rgba(16,185,129,0.2)'
                        : '1px solid rgba(245,158,11,0.25)',
                      borderRadius: '8px',
                      maxHeight: 120, overflow: 'auto',
                    }}>
                      <Typography sx={{
                        color: currentEvidence.page_value ? '#6ee7b7' : '#fbbf24',
                        fontFamily: currentEvidence.page_value ? 'monospace' : 'inherit',
                        fontSize: '0.875rem',
                        fontStyle: currentEvidence.page_value ? 'normal' : 'italic',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {currentEvidence.page_value
                          || 'Not present / not visible in hero canonical slot'}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Anomaly Categories — non-null specific; presence-gated. */}
                {currentEvidence.anomaly_categories && currentEvidence.anomaly_categories.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Anomaly Categories
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {currentEvidence.anomaly_categories.map((cat) => (
                        <Chip
                          key={cat}
                          label={cat.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            background: `${getCategoryColor(cat)}20`,
                            color: getCategoryColor(cat),
                            border: `1px solid ${getCategoryColor(cat)}40`,
                            fontWeight: 500, fontSize: '0.7rem',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Reason Evaluations — non-null specific; presence-gated. */}
                {currentEvidence.reason_evaluations && currentEvidence.reason_evaluations.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Reason Evaluations
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {currentEvidence.reason_evaluations.map((re, idx) => (
                        <Paper key={idx} sx={{
                          p: 1.5, borderRadius: '8px',
                          background: re.validated ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)',
                          border: `1px solid ${re.validated ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            {re.validated ? (
                              <CheckCircleIcon sx={{ color: '#ef4444', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                            ) : (
                              <CancelIcon sx={{ color: '#10b981', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.4 }}>
                                {re.reason}
                              </Typography>
                              {re.evidence && (
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, lineHeight: 1.4 }}>
                                  {re.evidence}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Chip
                                  label={re.validated ? 'Confirmed' : 'Refuted'}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: '0.65rem', fontWeight: 600,
                                    bgcolor: re.validated ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                                    color: re.validated ? '#ef4444' : '#10b981',
                                  }}
                                />
                                {re.source && (
                                  <Chip label={re.source} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Product info (search page type) */}
                {currentEvidence.product_name && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Product
                    </Typography>
                    <Paper sx={{
                      mt: 0.5,
                      p: 1,
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}>
                      <Typography sx={{ color: '#cbd5e1', fontWeight: 500, fontSize: '0.875rem', wordBreak: 'break-word' }}>
                        {currentEvidence.product_name}
                      </Typography>
                      {currentEvidence.product_id && (
                        <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mt: 0.5, fontFamily: 'monospace' }}>
                          ID: {currentEvidence.product_id}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {/* Reasoning — the one genuine, deliberate UX difference
                    (KTD3): collapsible for Non-Null (its reasoning text can
                    run long), plain/always-visible for Null. Kept as two
                    distinct behaviors rather than blended into a third. */}
                {isNonNull ? (
                  currentEvidence.reasoning && (
                    <Box sx={{ mb: 1.5 }}>
                      <Box
                        onClick={() => setReasoningExpanded((v) => !v)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Reasoning
                        </Typography>
                        {reasoningExpanded ? <ExpandLessIcon sx={{ color: '#64748b', fontSize: 16 }} /> : <ExpandMoreIcon sx={{ color: '#64748b', fontSize: 16 }} />}
                      </Box>
                      <Collapse in={reasoningExpanded}>
                        <Paper sx={{
                          mt: 0.5, p: 1.5, background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px',
                          maxHeight: 180, overflow: 'auto',
                        }}>
                          <Typography sx={{ color: '#cbd5e1', fontSize: '0.875rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {currentEvidence.reasoning}
                          </Typography>
                        </Paper>
                      </Collapse>
                    </Box>
                  )
                ) : (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Reasoning
                    </Typography>
                    <Paper sx={{
                      mt: 0.5,
                      p: 1.5,
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}>
                      <Typography sx={{
                        color: '#cbd5e1',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {currentEvidence.reasoning || 'No reasoning provided'}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>

              {/* Image thumbnails for navigation */}
              {allItems.length > 1 && (
                <Box sx={{
                  p: 1.25,
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(0, 0, 0, 0.2)'
                }}>
                  <Typography variant="caption" sx={{ color: '#64748b', mb: 0.5, display: 'block' }}>
                    All Evidence ({allItems.length})
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    py: 0.5,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { background: '#475569', borderRadius: 3 }
                  }}>
                    {stripItems.map(({ item, realIdx }) => {
                      const cls = localClassifications ? localClassifications[realIdx] : null;
                      const isActive = realIdx === realIndex;
                      const borderColor = isActive
                        ? '#3b82f6'
                        : cls?.verdict === 'true_anomaly'
                        ? '#10b981'
                        : cls?.verdict === 'false_positive'
                        ? '#ef4444'
                        : cls?.verdict === 'skipped'
                        ? 'rgba(100,116,139,0.3)'
                        : 'transparent';
                      return (
                        <Box
                          key={realIdx}
                          onClick={() => {
                            if (isActive) return;
                            if (isIncompleteFalsePositive(localClassificationsRef.current?.[realIndex])) return;
                            hookSetCurrentIndex(realIdx);
                            handleResetView();
                            setReasoningExpanded(false);
                          }}
                          sx={{
                            width: 60,
                            height: 45,
                            flexShrink: 0,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: `2px solid ${borderColor}`,
                            opacity: isActive ? 1 : 0.6,
                            transition: 'all 0.2s',
                            '&:hover': { opacity: 1 }
                          }}
                        >
                          {item?.url && item.url.startsWith('http') ? (
                            <Box
                              component="img"
                              src={item.url}
                              alt={`Thumbnail ${realIdx + 1}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.03)' }}>
                              <BrokenImageIcon sx={{ color: '#334155', fontSize: 14 }} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default EvidenceReviewModal;
